import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { addDaysIso, fmtDate, todayLocalIso } from '../../shared/dates';

/**
 * Discharges report — staff pulls clients discharged in a date range to
 * reconcile with the EMR. EMR-direction is the same surface in reverse:
 * the EMR can POST `/clients/:id/discharge?dischargedDate=YYYY-MM-DD`
 * (or PATCH `/clients/:id` with `{status: 'DISCHARGED', dischargedDate: ...}`)
 * and the rows will appear here on the next pull.
 *
 * "Export CSV" emits a one-row-per-client CSV with EMR id + program span +
 * attendance % — paste-friendly for the EMR import side, or attach to an
 * email when the EMR sync is manual.
 */
export function DischargesView() {
  const today = todayLocalIso();
  const [from, setFrom] = useState(addDaysIso(today, -30));
  const [to, setTo] = useState(today);

  const { data, isLoading } = useQuery({
    queryKey: ['discharges', from, to],
    queryFn: () =>
      api.get('/reports/discharges', { params: { from, to } }).then((r) => r.data),
  });

  const rows = (data?.rows ?? []) as any[];

  function downloadCsv() {
    if (rows.length === 0) return;
    const headers = [
      'Cohort ID',
      'EMR Client ID',
      'First Name',
      'Last Name',
      'Cohort',
      'Intake Date',
      'Discharged Date',
      'Days in Program',
      'Case Manager',
      'Case Manager Phone',
      'Scheduled',
      'Attended',
      'No Show',
      'Client Cancel',
      'Excused',
      'Attendance %',
      'Required Services',
    ];
    const csvRows = [headers.join(',')];
    for (const r of rows) {
      csvRows.push(
        [
          q(r.cohortId),
          q(r.externalRef ?? ''),
          q(r.firstName),
          q(r.lastName),
          q(r.cohort),
          q(r.intakeDate),
          q(r.dischargedDate),
          String(r.daysInProgram),
          q(r.caseManagerName ?? ''),
          q(r.caseManagerPhone ?? ''),
          String(r.attendance.scheduled),
          String(r.attendance.attended),
          String(r.attendance.noShow),
          String(r.attendance.cancelledClient),
          String(r.attendance.excused),
          String(r.attendance.attendancePct),
          q((r.requiredServices ?? []).join('; ')),
        ].join(','),
      );
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discharges-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const byCohortChips = useMemo(() => {
    const counts = data?.totals?.byCohort ?? {};
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Discharges</h2>
          <p className="text-xs text-ink-subtle">
            Clients whose discharge date falls in the window. Use Export CSV to sync to the EMR,
            or feed EMR-initiated discharges back via{' '}
            <code className="rounded bg-surface-muted px-1 py-0.5">
              POST /clients/:id/discharge
            </code>{' '}
            with an explicit <code>dischargedDate</code> — they appear here on the next refresh.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <DateInput label="From" value={from} onChange={setFrom} />
          <DateInput label="To" value={to} onChange={setTo} />
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -7));
              setTo(today);
            }}
          >
            7d
          </button>
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -30));
              setTo(today);
            }}
          >
            30d
          </button>
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -90));
              setTo(today);
            }}
          >
            90d
          </button>
          <button
            className="btn-primary h-9"
            disabled={rows.length === 0}
            onClick={downloadCsv}
          >
            Export CSV
          </button>
        </div>
      </header>

      {data && (
        <section className="card flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <span className="text-ink-subtle">
            Total <span className="font-semibold text-ink">{data.totals.clientCount}</span> discharges
          </span>
          {byCohortChips.length > 0 && (
            <span className="text-ink-subtle">
              By cohort:{' '}
              {byCohortChips.map(([cohort, count]) => (
                <span
                  key={cohort}
                  className="mr-1 inline-flex items-center gap-1 rounded bg-brand-50 px-2 py-0.5 text-2xs font-medium text-brand-800"
                >
                  {cohort}: {String(count)}
                </span>
              ))}
            </span>
          )}
          <span className="ml-auto text-2xs text-ink-subtle">
            {fmtDate(from)} → {fmtDate(to)}
          </span>
        </section>
      )}

      <section className="card overflow-hidden">
        {isLoading ? (
          <p className="p-4 text-sm text-ink-subtle">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-ink-subtle">
            No discharges in this window.
          </p>
        ) : (
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Cohort</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">EMR ID</th>
                <th className="px-3 py-2 text-left">Intake</th>
                <th className="px-3 py-2 text-left">Discharged</th>
                <th className="px-3 py-2 text-right">Days</th>
                <th className="px-3 py-2 text-left">Case manager</th>
                <th className="px-3 py-2 text-right">Sched</th>
                <th className="px-3 py-2 text-right">Att</th>
                <th className="px-3 py-2 text-right">Miss</th>
                <th className="px-3 py-2 text-right">Att %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.clientId} className="border-t border-line-subtle hover:bg-surface-subtle/40">
                  <td className="px-3 py-2 font-mono text-brand-700">{r.cohortId}</td>
                  <td className="px-3 py-2">
                    {r.firstName} {r.lastName}
                  </td>
                  <td className="px-3 py-2 font-mono text-2xs text-ink-subtle">
                    {r.externalRef ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-2xs">{r.intakeDate}</td>
                  <td className="px-3 py-2 font-mono text-2xs">{r.dischargedDate}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.daysInProgram}</td>
                  <td className="px-3 py-2 text-xs">{r.caseManagerName ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.attendance.scheduled}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.attendance.attended}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                    {r.attendance.noShow + r.attendance.cancelledClient}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={pctCls(r.attendance.attendancePct)}>
                      {r.attendance.attendancePct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col text-2xs text-ink-subtle">
      {label}
      <input
        type="date"
        className="input h-9"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function pctCls(pct: number): string {
  return clsx(
    'inline-block rounded px-2 py-0.5 text-2xs font-semibold tabular-nums',
    pct >= 90
      ? 'bg-emerald-100 text-emerald-800'
      : pct >= 75
        ? 'bg-amber-100 text-amber-800'
        : 'bg-rose-100 text-rose-800',
  );
}

// CSV-quote: wrap in double quotes if the cell contains a comma, quote, or newline,
// and escape inner quotes by doubling them.
function q(v: string): string {
  if (v == null) return '';
  if (/[",\n\r]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"';
  }
  return v;
}
